'use client';

import { useEventListener, useOnClickOutside, useScrollLock } from '@woonstad/helpers-hooks';
import { cn } from '@woonstad/helpers-utils';
import React, { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { isMobile as detectMobile } from 'react-device-detect';

import { BasicClose, BasicSearch, BasicSpinner } from '../../icons/basic';
import { DirectionsArrowRight, DirectionsChevronRight } from '../../icons/directions';
import { IconButton } from '../buttons/IconButton';
import { TextLink } from '../buttons/TextLink';
import { FeedbackError } from '../form/feedback/feedback-error';
import { labelRegularClasses, Typography } from '../typography/Typography';
import { SearchInput } from './SearchInput';

/**
 * `react-device-detect` is only accurate after `window` exists. Using `false` on the
 * first paint made every viewport render the desktop tree, then the hydration flipped
 * to the mobile tree — users saw a focusable desktop input while taps were handled by
 * the mobile overlay button after re-mount. `useSyncExternalStore` returns `null` on
 * the server and reads the real value on the client without a setState-in-effect
 * cascade, so the correct layout renders as soon as we know the breakpoint.
 */

const subscribeToBreakpoint = () => () => {
  // react-device-detect reads a synchronous user-agent string; nothing to subscribe to.
};

const getBreakpointSnapshot = () => detectMobile;
const getBreakpointServerSnapshot = () => null;

function useIsMobile() {
  return useSyncExternalStore(subscribeToBreakpoint, getBreakpointSnapshot, getBreakpointServerSnapshot);
}

export type ResultItem = {
  id: string | number;
  link: React.ReactElement;
  description?: string;
  path?: string[];
  isDisabled?: boolean;
  isDisabledText?: string;
}

type SearchInputProps = {
  value: string;
  setValue: (value: string) => void;
  clearInputLabel: string;
  placeholder: string;
  onKeyDownSearchInput?: (e: KeyboardEvent) => void;
  onSearchButtonClick?: (ev: React.MouseEvent<HTMLButtonElement>, inputRef: React.RefObject<HTMLInputElement | null>) => void;
  isOnDark?: boolean;
  isLoading?: boolean;
  results: ResultItem[];
  isAddressSearch?: boolean;
  offerResults?: Array<React.ReactNode>;
  autocomplete: string[];
  oftenSearched: ResultItem[];
  oftenSearchedText?: string;
  noResultsText?: string | React.ReactElement;
  hideOverLay?: boolean;
  hideOftenSearched?: boolean;
  variant?: Parameters<typeof SearchInput>[0]['variant'];
  hasError?: boolean;
  errorMessage?: string;
}

function getOverlayClasses(isOpen: boolean, hideOverLay?: boolean) {
  return cn(
    // Visual dimming only: a blocking overlay sits above in-page UI (e.g. aanbod filters in the
    // sidebar) and intercepts clicks without reliably closing first in automated tests. Clicks pass
    // through; leaving the search field still closes via focusout / Escape / outside-target handlers.
    'pointer-events-none fixed inset-0 z-[40] bg-surface-dimmer opacity-0 transition-opacity duration-300',
    isOpen && !hideOverLay && 'opacity-100',
    (!isOpen || hideOverLay) && 'hidden',
  );
}

function getCloseButtonClasses(isOpen: boolean) {
  return cn(
    'bg-transparent transition-all',
    !isOpen && 'w-0 scale-0 p-0 opacity-0',
    isOpen && 'ml-2 scale-100 opacity-100',
  );
}

export const SearchInputWithResults = (props: SearchInputProps) => {
  const isMobile = useIsMobile();

  if (isMobile === null) {
    return (
      <div
        aria-busy="true"
        aria-label={props.placeholder}
        className={cn(
          labelRegularClasses,
          'flex w-full items-center rounded-full',
          props.variant === 'large' ? 'h-14' : 'h-10',
          props.isOnDark
            ? 'bg-search-on-dark'
            : 'bg-search-on-light',
        )}
      />
    );
  }

  return <SearchInputWithResultsMounted {...props} isMobile={isMobile} />;
};

/**
 * Renders only once `isMobile` is known so refs exist before `useEventListener` runs.
 * If hooks ran while `isMobile === null`, the loading shell had no ref targets and
 * `useEventListener` fell through to `window` for the element overload — opening search on every click.
 */
function SearchInputWithResultsMounted({
  value,
  setValue,
  clearInputLabel,
  placeholder,
  isOnDark,
  isLoading,
  results,
  offerResults,
  autocomplete,
  isAddressSearch = false,
  oftenSearched,
  oftenSearchedText,
  hideOftenSearched,
  noResultsText,
  hideOverLay,
  onKeyDownSearchInput,
  onSearchButtonClick,
  hasError,
  errorMessage,
  variant = 'default',
  isMobile,
}: SearchInputProps & { isMobile: boolean }) {
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    searchResultsRef.current?.blur();
    searchInputRef.current?.blur();
    setValue('');
  }, [setValue]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    searchInputRef.current?.focus();
  }, []);

  // Do not use useEventListener('focus', …, searchInputRef): on first effect run ref.current
  // can still be null, so the listener attaches to `window` and never receives input focus
  // (focus does not bubble). Explicit onFocus on SearchInput is reliable for users and E2E.
  // Clicks on the mobile overlay use the button's onClick={handleOpen}; avoid duplicating with
  // useEventListener(ref) which used to fall back to window when ref was unset.

  const onFocusOutResults = useCallback((e: FocusEvent) => {
    const related = e.relatedTarget as Node | null;

    // Pointer focus often leaves relatedTarget null; closing here races handleOpen and
    // suppresses the overlay until a later focus cycle.
    if (related != null && searchResultsRef.current?.contains(related)) {
      return;
    }

    if (related == null) {
      window.requestAnimationFrame(() => {
        if (!searchResultsRef.current?.contains(document.activeElement)) {
          handleClose();
        }
      });

      return;
    }

    handleClose();
  }, [handleClose]);

  useEventListener('focusout', onFocusOutResults, searchResultsRef);

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }

    onKeyDownSearchInput?.(e);
  }, [handleClose, onKeyDownSearchInput]);

  useEventListener('keydown', onKeyDown, searchResultsRef);

  const onClickOutside = useCallback((e: MouseEvent | TouchEvent) => {
    if (isOpen) {
      e.stopPropagation();
      e.preventDefault();
      handleClose();
    }
  }, [handleClose, isOpen]);

  useOnClickOutside(searchResultsRef, onClickOutside);

  useScrollLock({ autoLock: isOpen });

  const sharedResultListProps = {
    isLoading: !!isLoading,
    offerResults,
    hideOftenSearched,
    oftenSearched,
    oftenSearchedText,
    noResultsText,
    hasError,
    errorMessage,
    results,
    isAddressSearch,
    autocomplete,
    value,
    setValue,
  };

  const searchInputOnButtonClick = isOpen && onSearchButtonClick
    ? (ev: React.MouseEvent<HTMLButtonElement>) => onSearchButtonClick(ev, searchInputRef)
    : undefined;

  if (isMobile) {
    return (
      <>
        <div className="relative">
          <SearchInput
            value={value}
            setValue={setValue}
            variant={variant}
            clearInputLabel={clearInputLabel}
            placeholder={placeholder}
            isOnDark={isOpen ? false : isOnDark}
            searchButtonTabIndex={-1}
            onButtonClick={searchInputOnButtonClick}
            onFocus={handleOpen}
          />
          <button
            ref={searchButtonRef}
            className="absolute left-0 top-0 hidden size-full bg-none max-sm:block"
            type="button"
            aria-label="Zoeken"
            onClick={handleOpen}
          />
        </div>

        {/* Keep mounted so searchResultsRef is never null; conditional mount made useEventListener fall back to window. */}
        <div
          data-testid="search-input-results-wrapper"
          data-testvalue={isOpen}
          ref={searchResultsRef}
          tabIndex={-1}
          hidden={!isOpen}
          className="fixed inset-4 z-50"
        >
          <div className="relative z-10 flex items-center transition-all">
            <SearchInput
              ref={searchInputRef}
              value={value}
              setValue={setValue}
              variant={variant}
              clearInputLabel={clearInputLabel}
              placeholder={placeholder}
              isOnDark={false}
              onFocus={(e) => {
                handleOpen();
                e.preventDefault();
                e.target.focus({ preventScroll: true });
              }}
              searchButtonTabIndex={-1}
              onButtonClick={searchInputOnButtonClick}
            />
            <IconButton
              type="button"
              icon={<BasicClose />}
              label="Sluit zoeken"
              aria-label="Sluit zoeken"
              onClick={handleClose}
              className={getCloseButtonClasses(isOpen)}
            />
          </div>

          <ResultList {...sharedResultListProps} isOpen={isOpen} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={getOverlayClasses(isOpen, hideOverLay)} />
      <div
        data-testid="search-input-results-wrapper"
        data-testvalue={isOpen}
        ref={searchResultsRef}
        tabIndex={-1}
        className={cn(
          'relative inset-0',
          isOpen && 'z-50 max-sm:fixed max-sm:inset-4',
          isAddressSearch && 'max-sm:relative max-sm:inset-0',
        )}
      >
        <div
          className={cn(
            'relative flex items-center transition-all',
            isOpen && 'z-10',
          )}
        >
          <SearchInput
            ref={searchInputRef}
            value={value}
            setValue={setValue}
            variant={variant}
            clearInputLabel={clearInputLabel}
            placeholder={placeholder}
            isOnDark={isOpen ? false : isOnDark}
            searchButtonTabIndex={-1}
            onButtonClick={searchInputOnButtonClick}
            onFocus={handleOpen}
          />
          <button
            ref={searchButtonRef}
            className="absolute hidden size-full bg-none max-sm:block"
            type="button"
            aria-label="Zoeken"
            onClick={handleOpen}
          />
          <IconButton
            type="button"
            icon={<BasicClose />}
            label="Sluit zoeken"
            onClick={handleClose}
            tabIndex={isOpen ? undefined : -1}
            className={getCloseButtonClasses(isOpen)}
          />
        </div>

        <ResultList {...sharedResultListProps} isOpen={isOpen} />
      </div>
    </>
  );
}

type ResultListProps = {
  isOpen: boolean;
  isLoading: boolean;
  offerResults?: Array<React.ReactNode>;
  hideOftenSearched?: boolean;
  oftenSearched: SearchInputProps['results'];
  oftenSearchedText?: string;
  noResultsText?: string | React.ReactElement;
  results: SearchInputProps['results'];
  isAddressSearch?: boolean;
  autocomplete: string[];
  value: string;
  setValue: (value: string) => void;
  hasError?: boolean;
  errorMessage?: string;
};

function shouldShowInitial(isLoading: boolean, value: string, hideOftenSearched?: boolean) {
  return !isLoading && value === '' && !hideOftenSearched;
}

function shouldShowEmpty(isLoading: boolean, hasError: boolean, value: string, resultsLength: number) {
  return !isLoading && !hasError && value !== '' && resultsLength === 0;
}

function shouldShowResults(isLoading: boolean, value: string, resultsLength: number) {
  return !isLoading && value !== '' && resultsLength > 0;
}

const ResultList = ({
  isOpen,
  isLoading,
  offerResults,
  hideOftenSearched,
  oftenSearched,
  oftenSearchedText,
  noResultsText,
  results,
  isAddressSearch,
  autocomplete,
  value,
  setValue,
  hasError,
  errorMessage,
}: ResultListProps) => {
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLOutputElement>(null);

  const onScrollResults = useCallback(() => {
    searchResultsRef.current?.focus();
  }, []);

  useEventListener('touchmove', onScrollResults, scrollableRef);

  const showInitial = shouldShowInitial(isLoading, value, hideOftenSearched);
  const showEmpty = shouldShowEmpty(isLoading, !!hasError, value, results.length);
  const showResults = shouldShowResults(isLoading, value, results.length);
  const showOfferResults = !isLoading && !!offerResults && offerResults.length > 0;

  return (
    <div
      className={cn(
        'absolute -inset-x-4 -top-4 w-[calc(100%+2rem)] rounded-md bg-search-results pt-20 shadow-large duration-150 has-[>div:empty]:shadow-none md:-inset-x-6 md:-top-6',
        !isOpen && 'invisible animate-out fade-out zoom-out-95 fill-mode-forwards',
        isOpen && 'visible animate-in fade-in zoom-in-95',
      )}
    >
      <output
        ref={scrollableRef}
        aria-live="polite"
        className={cn("block overflow-y-auto p-4 text-base-primary-on-light empty:hidden max-sm:h-[calc(100dvh-5rem)] sm:max-h-[calc(100svh-10rem)]", {
          'pt-0 max-sm:max-h-[500px] sm:max-h-[500px]': isAddressSearch,
        })}
      >
        {isLoading && <LoadingResults />}

        {!isLoading && hasError && <ResultError errorMessage={errorMessage} />}

        {showInitial && (
          <InitialResults
            oftenSearched={oftenSearched}
            oftenSearchedText={oftenSearchedText}
          />
        )}

        {showOfferResults && offerResults ? (
          <OfferResultsSection offerResults={offerResults} />
        ) : null}

        {showEmpty && <ResultEmpty noResultsText={noResultsText} />}

        {showResults && (
          <SearchResults
            autocomplete={autocomplete}
            results={results}
            value={value}
            setValue={setValue}
            searchResultsRef={searchResultsRef}
          />
        )}
      </output>
    </div>
  );
};

const OfferResultsSection = ({ offerResults }: { offerResults: Array<React.ReactNode> }) => (
  <div>
    <ul
      className="mb-6 grid grid-cols-1 gap-6 max-md:grid-cols-1 max-sm:grid-cols-2 max-xs:grid-cols-1 md:grid-cols-2"
      aria-label="Aanbod zoekresultaten"
    >
      {offerResults.map((offerResult) => offerResult)}
    </ul>

    <TextLink
      iconRight={DirectionsArrowRight}
      size="small"
    >
      {/* Using <a/> to not have a Next.js dependency in the design system */}
      <a href="/aanbod">
        Meer aanbod
      </a>
    </TextLink>

    <hr className="my-8 w-full text-divider" />
  </div>
);

const SearchResults = ({
  autocomplete,
  results,
  value,
  setValue,
  searchResultsRef,
}: {
  autocomplete: string[];
  results: SearchInputProps['results'];
  value: string;
  setValue: (value: string) => void;
  searchResultsRef: React.RefObject<HTMLDivElement | null>;
}) => (
  <>
    {autocomplete.length > 0 && (
      <ul className="mb-8 space-y-4">
        {autocomplete.map((suggestion, index) => (
          <li
            key={`${suggestion}-${index}`}
            className="flex items-center gap-2"
          >
            <BasicSearch className="size-4" />
            <button
              type="button"
              onMouseDown={(event) => {
                // Chrome fires blur before click; prevent focus from leaving input so
                // the focusout handler cannot clear the value before click applies it.
                event.preventDefault();
              }}
              onClick={() => {
                setValue(suggestion);
                searchResultsRef.current?.focus();
              }}
              aria-label={`Zoek op ${suggestion}`}
            >
              <Typography variant="content-body">
                {value}
                <strong>{[...suggestion].toSpliced(0, value.length).join('')}</strong>
              </Typography>
            </button>
          </li>
        ))}
      </ul>
    )}
    <ul
      className="space-y-8"
      aria-label="Zoekresultaten"
    >
      {results.map((result) => (
        <ResultItemComponent
          key={result.id}
          result={result}
        />
      ))}
    </ul>
  </>
);

const ResultItemComponent = ({
  result,
  testid,
}: {
  result: SearchInputProps['results'][number];
  testid?: string;
}) => (
  <li
    className="space-y-2"
  >
    <TextLink
      iconRight={DirectionsArrowRight}
      size="small"
      testid={testid}
    >
      {result.link}
    </TextLink>
    {result.description && (
      <Typography
        aria-hidden
        variant="content-body"
        className={cn("line-clamp-2 text-search-results", {
          'text-textlink-disabled-on-light': result.isDisabled,
        })}
      >
        {result.description}
      </Typography>
    )}
    {result.isDisabled && (
      <Typography
        variant="label-small"
        type="span"
        className="text-textlink-disabled-on-light"
      >
        {result.isDisabledText}
      </Typography>
    )}
    {result.path && (
      <div
        aria-hidden
        className="flex items-center gap-x-1 text-search-results-path"
      >
        {result.path.map((path, index) => (
          <React.Fragment key={`${path}-${index}`}>
            <Typography
              variant="label-small"
              type="span"
            >
              {path}
            </Typography>

            {index < (result.path?.length ?? 0) - 1 && (
              <DirectionsChevronRight className="size-3" />
            )}
          </React.Fragment>
        ))}
      </div>
    )}
  </li>
);

const LoadingResults = () => (
  <div
    className="flex h-28 items-center justify-center"
  >
    <BasicSpinner className="size-6 animate-spin" />
  </div>
);

const InitialResults = ({
  oftenSearched,
  oftenSearchedText,
}: {
  oftenSearched: SearchInputProps['results'];
  oftenSearchedText?: string;
}) => (
  <div
    className="space-y-4"
  >
    <Typography
      variant="content-body"
      textColorClassname="text-input-hint"
    >
      {oftenSearchedText || 'Vaak gezocht:'}
    </Typography>

    <ul className="space-y-4">
      {oftenSearched.map((result) => (
        <li
          key={result.id}
        >
          <TextLink
            iconRight={DirectionsArrowRight}
            size="small"
          >
            {result.link}
          </TextLink>
        </li>
      ))}
    </ul>
  </div>
);

const ResultEmpty = ({ noResultsText }: { noResultsText?: string | React.ReactElement }) => {
  if (!noResultsText || typeof noResultsText === 'string') {
    return (
      <div className="flex h-28 items-center justify-center bg-search-noresults p-4 text-center">
        <Typography
          variant="content-body"
          data-testid="noResultsText"
        >
          {noResultsText || 'Geen resultaten gevonden. Probeer een ander zoekwoord.'}
        </Typography>
      </div>
    );
  } else {
    return noResultsText;
  }
};

const ResultError = ({ errorMessage }: { errorMessage?: string }) => (
  <FeedbackError message={errorMessage || 'Er ging iets mis tijdens het zoeken, probeer het later nog een keer.'} />
);
